'use client';

import { useState, useEffect } from 'react';
import AppImage from '@/components/ui/AppImage';


interface BeforeAfter {
  id: number;
  before: string;
  after: string;
  beforeAlt: string;
  afterAlt: string;
  title: string;
  description: string;
  isSingleImage?: boolean;
  singleImage?: string;
  singleImageAlt?: string;
}

const BeforeAfterSection = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const transformations: BeforeAfter[] = [
  {
    id: 1,
    before: "/assets/images/paver-patio-cleaning-phx-az_1_-1765466898929.jpg",
    after: "/assets/images/paver-patio-cleaning-phx-az_1_-1765466898929.jpg",
    beforeAlt: 'Worker in yellow boots pressure washing residential paver patio with landscaping in Phoenix',
    afterAlt: 'Worker in yellow boots pressure washing residential paver patio with landscaping in Phoenix',
    title: 'Paver Patio Restoration',
    description: 'Complete transformation removing years of desert dust, stains, and weathering',
    isSingleImage: true,
    singleImage: "/assets/images/paver-patio-cleaning-phx-az_1_-1765466898929.jpg",
    singleImageAlt: 'Professional pressure washing service showing worker in yellow boots cleaning residential paver patio with desert landscaping in Phoenix Arizona'
  },
  {
    id: 2,
    before: "/assets/images/power-washing-patio-phx-az-1765466153176.jpg",
    after: "/assets/images/power-washing-patio-phx-az-1765466153176.jpg",
    beforeAlt: 'Professional power washing service on paver patio in Phoenix Arizona',
    afterAlt: 'Professional power washing service on paver patio in Phoenix Arizona',
    title: 'Professional Paver Cleaning',
    description: 'Expert pressure washing service for residential paver patios in Phoenix',
    isSingleImage: true,
    singleImage: "/assets/images/power-washing-patio-phx-az-1765466153176.jpg",
    singleImageAlt: 'Professional power washing equipment and worker providing expert paver cleaning service on residential patio in Phoenix Arizona'
  },
  {
    id: 3,
    before: "https://img.rocket.new/generatedImages/rocket_gen_img_1fe476a9a-1765440341109.png",
    after: "https://img.rocket.new/generatedImages/rocket_gen_img_1fe476a9a-1765440341109.png",
    beforeAlt: 'Pool deck with algae and calcium deposits before cleaning',
    afterAlt: 'Clean pool deck with non-slip treatment after professional service',
    title: 'Pool Deck Revival',
    description: 'Algae removal, stain treatment, and protective coating application'
  }];


  if (!isHydrated) {
    return (
      <section className="py-16 md:py-24 bg-muted">
        <div className="w-full px-4 md:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <div className="text-center">
              <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
              <p className="text-muted-foreground">Loading transformations...</p>
            </div>
          </div>
        </div>
      </section>);

  }

  return (
    <section className="py-16 md:py-24 bg-muted">
      <div className="w-full px-4 md:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          {/* Section Header */}
          <div className="text-center mb-12 md:mb-16">
            <h2 className="font-headline font-bold text-3xl md:text-4xl lg:text-5xl text-foreground mb-4">
              Paver Patio Cleaning Phoenix
            </h2>
            <p className="font-body text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto">
              See the dramatic difference our professional cleaning makes on Phoenix patios
            </p>
          </div>

          {/* Main Display */}
          <div className="bg-card rounded-lg shadow-brand-elevated overflow-hidden mb-8">
            {transformations[activeIndex].isSingleImage ? (
            /* Single Wide Image Display */
            <div className="relative h-80 md:h-96 lg:h-[500px] overflow-hidden">
                <AppImage
                src={transformations[activeIndex].singleImage || transformations[activeIndex].after}
                alt={transformations[activeIndex].singleImageAlt || transformations[activeIndex].afterAlt}
                className="w-full h-full object-cover" />

              </div>) : (

            /* Before/After Grid Display */
            <div className="grid grid-cols-1 lg:grid-cols-2">
                {/* Before Image */}
                <div className="relative h-80 md:h-96 lg:h-[500px] overflow-hidden">
                  <div className="absolute top-4 left-4 z-10 bg-destructive text-destructive-foreground px-4 py-2 rounded-md font-body font-semibold">
                    Before
                  </div>
                  <AppImage
                  src={transformations[activeIndex].before}
                  alt={transformations[activeIndex].beforeAlt}
                  className="w-full h-full object-cover" />

                </div>

                {/* After Image */}
                <div className="relative h-80 md:h-96 lg:h-[500px] overflow-hidden">
                  <div className="absolute top-4 left-4 z-10 bg-success text-success-foreground px-4 py-2 rounded-md font-body font-semibold">
                    After
                  </div>
                  <AppImage
                  src={transformations[activeIndex].after}
                  alt={transformations[activeIndex].afterAlt}
                  className="w-full h-full object-cover" />

                </div>
              </div>)
            }

            {/* Description */}
            <div className="p-6 md:p-8 bg-card">
              <h3 className="font-headline font-semibold text-2xl text-card-foreground mb-3">
                {transformations[activeIndex].title}
              </h3>
              <p className="font-body text-muted-foreground text-lg">
                {transformations[activeIndex].description}
              </p>
            </div>
          </div>

          {/* Thumbnail Navigation */}
          <div className="grid grid-cols-3 gap-4">
            {transformations.map((item, index) =>
            <button
              key={item.id}
              onClick={() => setActiveIndex(index)}
              className={`relative rounded-lg overflow-hidden transition-brand ${
              index === activeIndex ?
              'ring-4 ring-primary shadow-brand-elevated' :
              'opacity-70 hover:opacity-100'}`
              }>

                <div className="aspect-video">
                  <AppImage
                  src={item.isSingleImage ? item.singleImage || item.after : item.after}
                  alt={`Thumbnail for ${item.title}`}
                  className="w-full h-full object-cover" />

                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent flex items-end p-3">
                  <p className="font-body font-medium text-white text-sm">{item.title}</p>
                </div>
              </button>
            )}
          </div>
        </div>
      </div>
    </section>);

};

export default BeforeAfterSection;